/**
 * API service integrations for the Salzburg Cab Day Trips pages.
 * Implements real network requests to the backend server with standard headers and error handling.
 * Fallback local mock data is provided with a simple switch to enable/disable.
 */

import { mockDayTrips } from '../../mocks/dayTripMock';

export interface DayTrip {
  id: string;
  title: string;
  subtitle: string;
  shortDescription: string; // Dynamic field for Tour Overview
  itinerary: string;
  startLocation: string;
  endLocation: string;
  duration: string;         // e.g. "1 hour 30 minutes"
  maxPeople: number;
  highlights: string[];
  excludedFeatures?: string[];
  excluded_features?: string[];
  goodToKnow: string[];
  images: string[];
  price: number;

  overview?: string;
  time?: string;
  image?: string;
  tags?: string[];
  categories?: string[];
  routeQuery?: string;
  active?: boolean;
  showOnHomepage?: boolean;
}

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  warning?: string;
}

/**
 * Backend API Configurations.
 * Define VITE_API_BASE_URL in your .env file to override the default.
 */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://72.61.92.71:8000';
const API_PATH_URL = `${API_BASE_URL}/api/admin`;

export const USE_REAL_BACKEND = true;

export function shouldShowOnHomepage(item?: any): boolean {
  const value =
    item?.showOnHomepage ??
    item?.show_on_homepage ??
    item?.showOnHomePage;

  const showOnHomepage = typeof value === 'boolean'
    ? value
    : typeof value === 'number'
      ? value === 1
      : typeof value === 'string'
        ? ['true', '1', 'yes'].includes(value.trim().toLowerCase())
        : false;

  const activeValue = item?.active ?? item?.is_active;
  const active = typeof activeValue === 'boolean'
    ? activeValue
    : typeof activeValue === 'number'
      ? activeValue === 1
      : typeof activeValue === 'string'
        ? ['true', '1', 'yes', 'active'].includes(activeValue.trim().toLowerCase())
        : true;

  return active && showOnHomepage;
}

/** Normalize backend/mock payloads so subtitle + shortDescription always exist. */
export function normalizeDayTrip(
  raw: Partial<DayTrip> | Record<string, unknown>,
): DayTrip {
  const data = raw as Record<string, unknown>

  const parseBooleanValue = (value: unknown): boolean | undefined => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();

      if (['true', '1', 'yes'].includes(normalized)) return true;
      if (['false', '0', 'no'].includes(normalized)) return false;
    }

    return undefined;
  };

  const parseArrayField = (val: unknown): string[] => {
    if (Array.isArray(val)) {
      return val
        .map((item) => {
          if (typeof item === 'string') return item;

          if (!item) return '';

          if (typeof item === 'object') {
            const objectItem = item as Record<string, unknown>;

            return (
              objectItem.description ??
              objectItem.name ??
              objectItem.title ??
              objectItem.label ??
              JSON.stringify(item)
            );
          }

          return String(item);
        })
        .map((s) => s && String(s).trim())
        .filter(Boolean);
    }

    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);

        if (Array.isArray(parsed)) {
          return parseArrayField(parsed);
        }

        return val
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      } catch (e) {
        return val
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      }
    }

    return [];
  };

  const categories = parseArrayField(
    data.categories ??
    data.category_list ??
    data.tags
  );

  let tags = parseArrayField(
    data.tags ??
    data.categories ??
    data.category_list
  );

  if (tags.length === 0 && categories.length > 0) {
    tags = categories;
  }

  const images =
    Array.isArray(data.images) && data.images.length > 0
      ? data.images
          .map((img: unknown) =>
            typeof img === 'string'
              ? img
              : (img as Record<string, unknown>)?.image
          )
          .filter(Boolean)
      : Array.isArray(data.gallery_images) && data.gallery_images.length > 0
        ? data.gallery_images
            .map((img: unknown) =>
              typeof img === 'string'
                ? img
                : (img as Record<string, unknown>)?.image
            )
            .filter(Boolean)
        : [];

  const singleImage =
    data.image ??
    data.cover_image ??
    data.thumbnail ??
    (images.length > 0 ? images[0] : '');

  const priceRaw =
    data.price ??
    data.basePrice ??
    data.base_price ??
    data.extraPrice ??
    data.extra_price ??
    480;

  const priceNum =
    typeof priceRaw === 'number'
      ? priceRaw
      : Number(priceRaw) || 480;

  const title =
    data.title ??
    data.end_location ??
    data.endLocation ??
    'Day Trip';

  const subtitle =
    data.subtitle ||
    data.tagline ||
    data.location ||
    'Private Day Trip from Salzburg';

  const shortDescription =
    data.shortDescription ??
    data.short_description ??
    data.description ??
    data.overview ??
    '';

  const overview =
    data.overview ??
    data.shortDescription ??
    data.short_description ??
    data.description ??
    '';

  const itinerary = data.itinerary ?? '';

  const duration =
    data.duration ??
    data.time ??
    '8–10 Hours';

  const time =
    data.time ??
    data.duration ??
    '8–10 Hours';

  const routeQuery =
    data.routeQuery ??
    data.route_query ??
    data.location ??
    '';

  const startLocation =
    data.startLocation ??
    data.start_location ??
    'Salzburg';

  const endLocation =
    data.endLocation ??
    data.end_location ??
    data.title ??
    '';

  const maxPeople =
    Number(
      data.maxPeople ??
      data.max_people ??
      data.people ??
      8
    ) || 8;

  const highlights =
    parseArrayField(
      data.highlights ??
      data.trip_highlights ??
      data.features
    );

  const excludedFeatures =
    parseArrayField(
      data.excludedFeatures ??
      data.excluded_features ??
      data.excluded_features_rel
    );

  const goodToKnow =
    parseArrayField(
      data.goodToKnow ??
      data.good_to_know ??
      data.good_to_knows
    );

  const active =
    parseBooleanValue(data.active) ??
    parseBooleanValue(data.is_active) ??
    true;

  const showOnHomepage =
    parseBooleanValue(data.showOnHomepage) ??
    parseBooleanValue(data.show_on_homepage) ??
    false;

  return {
    id: String(data.id ?? ''),
    title: String(title),

    subtitle: String(subtitle),

    shortDescription: String(shortDescription),

    startLocation: String(startLocation),

    endLocation: String(endLocation),

    duration: String(duration),

    maxPeople,

    highlights,

    excludedFeatures,

    excluded_features: excludedFeatures,

    goodToKnow,

    images:
      images.length > 0
        ? images as string[]
        : [String(singleImage || '/assets/images/day-trip-hallstatt.png')],

    price: priceNum,

    overview: String(overview),

    itinerary: String(itinerary),

    time: String(time),

    image: String(
      singleImage ||
      '/assets/images/day-trip-hallstatt.png'
    ),

    tags:
      tags.length > 0
        ? tags
        : highlights,

    categories,

    routeQuery: String(routeQuery),

    active,

    showOnHomepage,
  }
}

export async function apiGetDayTrips(): Promise<ApiResponse<DayTrip[]>> {
  if (!USE_REAL_BACKEND) {
    return {
      success: true,
      message: 'Mock data loaded successfully.',
      data: mockDayTrips.map((trip) => normalizeDayTrip(trip)),
    };
  }

  try {
    const response = await fetch(`${API_PATH_URL}/day-trips/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    const result = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        message: result?.message || 'Failed to fetch day trips.'
      };
    }

    const list = Array.isArray(result?.results)
      ? result.results
      : Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result)
          ? result
          : []

    return {
      success: true,
      message: result?.message || 'Day trips loaded successfully.',
      data: list.map((trip: Record<string, unknown>) => normalizeDayTrip(trip)),
    };
  } catch (error) {
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to reach day trips API at ${API_PATH_URL}. Falling back to mock data could be configured.`
    };
  }
}


export async function apiGetDayTripById(id: string): Promise<ApiResponse<DayTrip>> {
  if (!USE_REAL_BACKEND) {
    const found = mockDayTrips.find(t => String(t.id) === String(id));
    if (found) {
      return {
        success: true,
        message: 'Mock detail loaded successfully.',
        data: normalizeDayTrip(found),
      };
    }
    return {
      success: false,
      message: 'Trip not found in mock data.'
    };
  }

  try {
    const response = await fetch(`${API_PATH_URL}/day-trips/${id}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    const result = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        message: result?.message || `Failed to fetch day trip with id ${id}.`
      };
    }

    const raw = result?.data || result

    return {
      success: true,
      message: result?.message || 'Day trip details loaded successfully.',
      data: normalizeDayTrip(raw),
    };
  } catch (error) {
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to reach day trip details API at ${API_PATH_URL}.`
    };
  }
}